import { useEffect, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import { Button } from '../../../components/ui/button';
import DataTable from '../../../components/ui/data-table/data-table';
import { createActionsColumn } from '../../../components/ui/data-table/columns';
import { SearchInput } from '../../../components/ui/search-input';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import taxService from 'services/TaxService';
import { FormDialog } from '../../../components/ui/form-dialog';
import AddTaxForm from '../../../components/TaxForm/AddTaxForm';
import { NewPagination } from '../../../components/ui/newPagination';
import CommonDialog from 'components/commonDialog/CommonDialogV2';
import { isSuperAdmin } from 'utils/jwtRole';

const DEFAULT_FORM_DATA = {
  name: '',
  percentage: '',
  isActive: true,
};

// ─── Static columns (always visible, same style as Toll Management) ────────────
const STATIC_DATA_COLUMNS = [
  { accessorKey: 'name', header: 'Tax Name' },
  { accessorKey: 'percentage', header: 'Value (%)' },
  // {
  //   accessorKey: "isActive",
  //   header: "Status",
  //   cell: ({ getValue }) => (
  //     <span
  //       className={`px-2 py-1 rounded-full text-xs font-semibold ${
  //         getValue() ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
  //       }`}
  //     >
  //       {getValue() ? "Active" : "Inactive"}
  //     </span>
  //   ),
  // },
];

export default function TaxConfiguration() {
  const [taxData, setTaxData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog — add / edit
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTaxId, setEditingTaxId] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM_DATA });
  const [nameError, setNameError] = useState('');

  // Dialog — delete
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTaxId, setDeleteTaxId] = useState(null);
  const [deleteTaxName, setDeleteTaxName] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // ─── Build columns once (static headers + live action handlers) ─────────────
  const buildColumns = useCallback(
    (onEdit, onDelete) => [
      ...STATIC_DATA_COLUMNS,
      createActionsColumn(
        [
          { label: 'Edit', onClick: onEdit },
          { label: 'Delete', onClick: onDelete },
        ],
        true,
      ),
    ],
    [],
  );

  const [columns, setColumns] = useState(() =>
    buildColumns(
      () => {},
      () => {},
    ),
  );

  // ─── Fetch ─────────────────────────────────────────────────────────────────

  const fetchTaxTypes = async (search = searchQuery) => {
    try {
      // API: GET /admin-api/v3/tax-masters  — no pagination params
      const query = {};
      if (search) {
        query.search = encodeURIComponent(search.trim());
      }

      const res = await taxService.getTaxList(query);
      // Response: { success, data: [...], message }
      const body = res?.data || [];
      setTotalItems(body.length);
      setTaxData(body);
      setFilteredData(body);

      // Rebuild columns with live handlers bound to fresh state closures
      setColumns(
        buildColumns(
          (row) => {
            setFormData({
              name: row.name,
              percentage: row.percentage,
              isActive: row.isActive ?? true,
            });
            setEditingTaxId(row.id);
            setIsEditing(true);
            setNameError('');
            setIsFormOpen(true);
          },
          (row) => {
            setDeleteTaxId(row.id);
            setDeleteTaxName(row.name);
            setIsDeleteDialogOpen(true);
          },
        ),
      );
    } catch (error) {
      toast.error('Failed to fetch tax types');
    }
  };

  useEffect(() => {
    fetchTaxTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTaxTypes(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // ─── Search ─────────────────────────────────────────────────────────────────

  const debouncedFetch = useCallback(
    debounce((q) => fetchTaxTypes(q), 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleSearch = (query) => {
    setSearchQuery(query || '');
    debouncedFetch(query || '');
  };

  useEffect(() => {
    return () => debouncedFetch.cancel();
  }, [debouncedFetch]);

  // ─── Pagination (client-side, API returns full list) ─────────────────────────

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };

  // ─── Add / Edit ──────────────────────────────────────────────────────────────

  const handleOpenAdd = () => {
    setIsEditing(false);
    setEditingTaxId(null);
    setFormData({ ...DEFAULT_FORM_DATA });
    setNameError('');
    setIsFormOpen(true);
  };

  const handleCancel = () => {
    setFormData({ ...DEFAULT_FORM_DATA });
    setIsEditing(false);
    setEditingTaxId(null);
    setNameError('');
    setIsFormOpen(false);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.percentage || nameError) return;

    try {
      // API only accepts name + percentage in the body
      const payload = {
        name: formData.name.trim(),
        percentage: parseFloat(formData.percentage),
      };

      if (isEditing && editingTaxId) {
        const res = await taxService.updateTax(editingTaxId, payload);
        toast.success(res?.message || 'Tax master updated successfully');
      } else {
        const res = await taxService.createTax(payload);
        toast.success(res?.message || 'Tax master created successfully');
      }

      setFormData({ ...DEFAULT_FORM_DATA });
      setIsEditing(false);
      setEditingTaxId(null);
      setNameError('');
      setIsFormOpen(false);
      setCurrentPage(1);
      fetchTaxTypes(searchQuery);
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        error?.message ||
        (isEditing ? 'Failed to update tax type' : 'Failed to create tax type');
      toast.error(apiMessage);
    }
  };

  // ─── Disable ─────────────────────────────────────────────────────────────────
  // API has no DELETE — uses PATCH /:id/disable (soft disable)

  const handleDeleteConfirm = async () => {
    try {
      const res = await taxService.disableTax(deleteTaxId);
      toast.success(res?.message || `"${deleteTaxName}" disabled successfully`);
      setIsDeleteDialogOpen(false);
      fetchTaxTypes(searchQuery);
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        error?.message ||
        'Failed to disable tax master';
      toast.error(apiMessage);
      setIsDeleteDialogOpen(false);
    }
  };

  // ─── Submit guard ────────────────────────────────────────────────────────────

  const isSubmitDisabled = !formData.name.trim() || !formData.percentage || !!nameError;

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto pt-4 pb-0">
      <h1 className="text-xl mb-6 text-[color:var(--brand-primary)]">Tax Configuration</h1>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-[15px]">
        {/* Left: search */}
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          <SearchInput
            onSearch={handleSearch}
            className="flex-1 lg:flex-none w-full lg:w-[250px]"
            placeholder="Tax Name"
          />
        </div>

        {/* Right: Add button */}
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          {/* QA #17/P1: only the platform super-admin (role 99) can mutate global tax masters; hide for tenant admins to avoid the 400. */}
          {isSuperAdmin() && (
            <FormDialog
              trigger={
                <Button
                  className="h-[40px] bg-[color:var(--brand-accent)] hover:bg-[color:var(--brand-accent)] rounded-[5px] font-normal text-[14px] text-white border border-white capitalize w-full lg:w-auto"
                  onClick={handleOpenAdd}
                >
                  + Add Tax Type
                </Button>
              }
              title={isEditing ? 'Edit' : 'Add'}
              subtitle="Tax Type"
              isOpen={isFormOpen}
              onOpenChange={setIsFormOpen}
              onSubmit={handleSave}
              onCancel={handleCancel}
              submitLabel={isEditing ? 'Update' : 'Create'}
              submitDisabled={isSubmitDisabled}
            >
              <AddTaxForm
                formData={formData}
                onFormDataChange={setFormData}
                existingNames={taxData}
                editingId={editingTaxId}
                nameError={nameError}
                onNameErrorChange={setNameError}
              />
            </FormDialog>
          )}
        </div>
      </div>

      {/* Table */}
      <div>
        <DataTable columns={columns} data={filteredData} onRowClick={() => {}} />
        <NewPagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          className="border-t"
        />
      </div>

      {/* Disable Confirm Dialog */}
      <CommonDialog
        open={isDeleteDialogOpen}
        handleClose={() => setIsDeleteDialogOpen(false)}
        toggle={setIsDeleteDialogOpen}
        onConfirmDialog={handleDeleteConfirm}
        text={`Are you sure you want to disable "${deleteTaxName}"?`}
        selectType="delete"
        dialogContent="taxDisable"
        typeOfDialog="Tax Master"
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}
